import { ButtonBase, Stack, Typography, useTheme } from "@mui/material";
import type { JourneyCase } from "../dtos";
import { caseStatusLabel } from "../utils/case-selection";

/** Lifecycle ink for a chip's caption — the same pairs `CaseStatusPills` paints for a terminal case. */
const statusInk = <T,>(row: JourneyCase, accent: T): T | string => {
  if (row.terminatedAt || row.status === "CANCELLED") return "#313131";
  if (row.status === "COMPLETED") return "#326054";
  return accent;
};

/**
 * §Multi-application: one chip per application on this lead, oldest first ("Application 1" is the first one
 * started), each captioned with its lifecycle status and current stage. Renders nothing for a single
 * application — the page then looks exactly as it did before a lead could run several.
 *
 * Plain `ButtonBase`, never MUI `Button`: the tab freezes every `.MuiButton-root` while a write is in flight
 * (§FlowBusy), and switching which application you are LOOKING at must stay possible during an autosave.
 */
const CaseSwitcher = ({
  cases,
  selectedUid,
  onSelect,
}: {
  cases: JourneyCase[];
  selectedUid: string | null;
  onSelect: (caseUid: string) => void;
}) => {
  const theme = useTheme();
  const colors = theme.nawy.colors;
  if (cases.length < 2) return null;

  return (
    <Stack
      direction="row"
      spacing={1}
      role="tablist"
      aria-label="Applications on this lead"
      sx={{
        overflowX: "auto",
        minWidth: 0,
        // the chips scroll sideways at 390px instead of widening the page
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
      }}
    >
      {cases.map((row, index) => {
        const selected = row.uid === selectedUid;
        const caption = [caseStatusLabel(row), row.currentStage ?? "—"].join(" · ");
        return (
          <ButtonBase
            key={row.uid}
            role="tab"
            aria-selected={selected}
            data-case-uid={row.uid}
            onClick={() => {
              if (!selected) onSelect(row.uid);
            }}
            sx={{
              flexShrink: 0,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 0.25,
              px: 1.5,
              py: 0.75,
              borderRadius: theme.nawy.radius.lg,
              border: `1px solid ${
                selected ? colors.semantic.text.accent : colors.semantic.border.secondary
              }`,
              backgroundColor: selected
                ? colors.semantic.layer.accentLight
                : colors.semantic.layer.primary,
              textAlign: "left",
            }}
          >
            <Typography
              sx={{
                fontSize: 13,
                lineHeight: "18px",
                fontWeight: 600,
                color: selected ? colors.semantic.text.accent : colors.semantic.text.primary,
                whiteSpace: "nowrap",
              }}
            >
              Application {index + 1}
              {row.onHoldAt ? " · On hold" : ""}
            </Typography>
            <Typography
              sx={{
                fontSize: 11,
                lineHeight: "16px",
                color: statusInk(row, colors.semantic.text.accent),
                whiteSpace: "nowrap",
              }}
            >
              {caption}
            </Typography>
          </ButtonBase>
        );
      })}
    </Stack>
  );
};

export default CaseSwitcher;
